import React, { useEffect, useRef, useState } from "react";
import { api, getErr, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash, UploadSimple, Eye, EyeSlash, ArrowSquareOut } from "@phosphor-icons/react";

const BLANK = {
  title: "", area: "", duration: "", services: [], stats: [{ value: "", label: "" }, { value: "", label: "" }, { value: "", label: "" }],
  situation: "", approach: "", outcome: "", quote: "", quote_attribution: "", published: false, order: 1,
};

const Field = ({ label, hint, children }) => (
  <label className="mt-3 block">
    <div className="text-xs font-bold">{label}{hint && <span className="ml-1 font-normal text-[#4B5563]">{hint}</span>}</div>
    <div className="mt-1">{children}</div>
  </label>
);

const ImageSlot = ({ cs, role, onChange }) => {
  const ref = useRef(null);
  const id = cs[`${role}_id`];
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("role", role);
    fd.append("file", file);
    try {
      await api.post(`/fresh-start/admin/case-studies/${cs.case_id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${role} photo saved (filename discarded — opaque id only).`);
      onChange();
    } catch (e) {
      toast.error(getErr(e));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/fresh-start/admin/case-studies/${cs.case_id}/image/${role}`);
      onChange();
    } catch (e) {
      toast.error(getErr(e));
    }
  };
  return (
    <div className="border border-[#E5E7EB] p-2" data-testid={`cs-image-${role}-${cs.case_id}`}>
      <div className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">{role}</div>
      {id ? (
        <>
          <img src={`${BACKEND_URL}/api/fresh-start/case-studies/${id}.jpg`} alt={`${role} photo`} className="mt-1 aspect-[4/3] w-full object-cover" />
          <button className="mt-1 text-[11px] font-bold text-[#EF4444]" onClick={remove} data-testid={`cs-image-remove-${role}-${cs.case_id}`}>Remove</button>
        </>
      ) : (
        <>
          <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`cs-image-input-${role}-${cs.case_id}`} />
          <button className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-[#0044FF]" onClick={() => ref.current?.click()} data-testid={`cs-image-upload-${role}-${cs.case_id}`}>
            <UploadSimple size={12} /> Upload real job photo
          </button>
        </>
      )}
    </div>
  );
};

const Editor = ({ cs, onSaved, onCancel, reload }) => {
  const [f, setF] = useState({ ...BLANK, ...cs, stats: [...(cs.stats || []), ...BLANK.stats].slice(0, 4) });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const setStat = (i, k, v) => { const s = [...f.stats]; s[i] = { ...s[i], [k]: v }; set("stats", s); };
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, services: typeof f.services === "string" ? f.services.split(",") : f.services };
      delete body.before_id; delete body.after_id; delete body.case_id; delete body.created_at; delete body.updated_at;
      await api.put(`/fresh-start/admin/case-studies/${cs.case_id}`, body);
      toast.success("Saved — live on /fresh-start now, and baked into the static page for the next deploy.");
      onSaved();
    } catch (e) {
      toast.error(getErr(e));
    }
    setSaving(false);
  };
  return (
    <div className="mt-3 border border-[#0044FF]/30 bg-[#F8FAFF] p-4" data-testid={`cs-editor-${cs.case_id}`}>
      <Field label="Title"><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="cs-title" /></Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Area" hint="(general only — e.g. West Baltimore rowhome)"><Input value={f.area} onChange={(e) => set("area", e.target.value)} data-testid="cs-area" /></Field>
        <Field label="Duration" hint="(e.g. One day)"><Input value={f.duration} onChange={(e) => set("duration", e.target.value)} data-testid="cs-duration" /></Field>
      </div>
      <Field label="Service tags" hint="(comma-separated)">
        <Input value={Array.isArray(f.services) ? f.services.join(", ") : f.services} onChange={(e) => set("services", e.target.value)} data-testid="cs-services" />
      </Field>
      <div className="mt-3 text-xs font-bold">Stat row <span className="font-normal text-[#4B5563]">(2–4 pairs; blank values are dropped)</span></div>
      {f.stats.map((s, i) => (
        <div key={i} className="mt-1 grid grid-cols-[90px_1fr] gap-2">
          <Input value={s.value} placeholder="3" onChange={(e) => setStat(i, "value", e.target.value)} data-testid={`cs-stat-value-${i}`} />
          <Input value={s.label} placeholder="truckloads removed" onChange={(e) => setStat(i, "label", e.target.value)} data-testid={`cs-stat-label-${i}`} />
        </div>
      ))}
      <Field label="The situation" hint="(2–3 sentences — no names, addresses, ZIPs or household details)"><Textarea rows={3} value={f.situation} onChange={(e) => set("situation", e.target.value)} data-testid="cs-situation" /></Field>
      <Field label="What we did" hint="(3–4 sentences — clear out → deep clean → carpet & odor; client decided what stayed)"><Textarea rows={4} value={f.approach} onChange={(e) => set("approach", e.target.value)} data-testid="cs-approach" /></Field>
      <Field label="The result" hint="(2–3 sentences)"><Textarea rows={3} value={f.outcome} onChange={(e) => set("outcome", e.target.value)} data-testid="cs-outcome" /></Field>
      <Field label="Client quote" hint="(optional — ONLY with written permission; leave blank otherwise)"><Textarea rows={2} value={f.quote} onChange={(e) => set("quote", e.target.value)} data-testid="cs-quote" /></Field>
      <Field label="Quote attribution" hint='(e.g. "Homeowner, West Baltimore")'><Input value={f.quote_attribution} onChange={(e) => set("quote_attribution", e.target.value)} data-testid="cs-quote-attribution" /></Field>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <ImageSlot cs={cs} role="before" onChange={reload} />
        <ImageSlot cs={cs} role="after" onChange={reload} />
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!f.published} onChange={(e) => set("published", e.target.checked)} data-testid="cs-published" />
          <b>Published</b> <span className="text-[#4B5563]">(unpublishing the only record hides the whole section)</span>
        </label>
        <Input type="number" className="w-20" value={f.order} onChange={(e) => set("order", Number(e.target.value))} title="Display order" data-testid="cs-order" />
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} data-testid="cs-cancel">Cancel</Button>
          <Button size="sm" disabled={saving} onClick={save} data-testid="cs-save">{saving ? "Saving…" : "Save case study"}</Button>
        </div>
      </div>
    </div>
  );
};

export const FreshStartCaseStudies = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/fresh-start/admin/case-studies");
      setItems(data.items || []);
    } catch (e) {
      toast.error(getErr(e));
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const { data } = await api.post("/fresh-start/admin/case-studies", { ...BLANK, title: "New case study", stats: [] });
      await load();
      setEditing(data.item.case_id);
    } catch (e) {
      toast.error(getErr(e));
    }
  };
  const togglePublished = async (cs) => {
    try {
      const body = { ...cs, published: !cs.published };
      delete body.before_id; delete body.after_id; delete body.case_id; delete body.created_at; delete body.updated_at;
      await api.put(`/fresh-start/admin/case-studies/${cs.case_id}`, body);
      toast.success(cs.published ? "Unpublished." : "Published.");
      load();
    } catch (e) {
      toast.error(getErr(e));
    }
  };
  const remove = async (cs) => {
    if (!window.confirm(`Delete "${cs.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/fresh-start/admin/case-studies/${cs.case_id}`);
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error(getErr(e));
    }
  };

  if (!items) return <div className="text-xs text-[#4B5563]">Loading…</div>;
  const published = items.filter((x) => x.published).length;
  return (
    <div data-testid="cs-manager">
      <p className="text-sm text-[#4B5563]">
        Anonymized "Real Work" stories shown under the Before &amp; After gallery on{" "}
        <a className="font-bold text-[#0044FF]" href="/fresh-start#real-work" target="_blank" rel="noreferrer">/fresh-start <ArrowSquareOut className="inline" size={11} /></a>.
        {" "}{published} published · {items.length} total. One record renders as a single card (no carousel chrome); zero published hides the section. Never include names, initials, addresses, ZIPs or anything that could identify a household — and no stock photos.
      </p>
      <div className="mt-3 space-y-2">
        {items.map((cs) => (
          <div key={cs.case_id} className="border border-[#E5E7EB] p-3" data-testid={`cs-row-${cs.case_id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`px-2 py-0.5 text-[10px] font-bold tracking-widest ${cs.published ? "bg-[#22C55E] text-white" : "bg-[#F3F4F6] text-[#4B5563]"}`} data-testid={`cs-status-${cs.case_id}`}>
                {cs.published ? "PUBLISHED" : "DRAFT"}
              </span>
              <span className="font-bold">{cs.title}</span>
              <span className="text-xs text-[#4B5563]">{[cs.area, cs.duration].filter(Boolean).join(" · ")}</span>
              <div className="ml-auto flex gap-1">
                <Button variant="outline" size="sm" onClick={() => togglePublished(cs)} title={cs.published ? "Unpublish" : "Publish"} data-testid={`cs-toggle-${cs.case_id}`}>
                  {cs.published ? <EyeSlash size={13} /> : <Eye size={13} />}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditing(editing === cs.case_id ? null : cs.case_id)} data-testid={`cs-edit-${cs.case_id}`}>
                  {editing === cs.case_id ? "Close" : "Edit"}
                </Button>
                <Button variant="outline" size="sm" className="text-[#EF4444]" onClick={() => remove(cs)} data-testid={`cs-delete-${cs.case_id}`}><Trash size={13} /></Button>
              </div>
            </div>
            {editing === cs.case_id && (
              <Editor cs={cs} reload={load} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
            )}
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" className="mt-3" onClick={create} data-testid="cs-new">
        <Plus size={12} /> New case study (starts as a draft)
      </Button>
    </div>
  );
};
